/* eslint-disable eqeqeq */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetSizerCabinetDimensionsQuery,
  useGetSizerCasingFlangeWidthsQuery,
  useGetSizerCasingInsulationsQuery,
  useGetSizerCoilFhsQuery,
  useGetSizerConstructionClassesQuery,
  useGetSizerCoolingCoilSettingsQuery,
  useGetSizerDeviationQuery,
  useGetSizerFilterSizesQuery,
  useGetSizerHeatingCoilSettingsQuery,
  useGetSizerOptimalSizesQuery,
  useGetSizerPageSizeQuery,
  useGetSizerPagesCountQuery,
  useGetSizerTubeDiasQuery,
  type ISizerFilterSize,
  type ISizerMasterRow,
  type ISizerMatrixRequest,
} from 'app/services/sizerApi';
import {
  readSizerSettings,
  writeSizerSettings,
  type SizerSettingsCache,
} from './sizerStorage';
import {
  applyCachedValues,
  buildMatrixPayload,
  buildMaxFhOptions,
  buildOptionSets,
  buildTemplateBounds,
  buildTemplateValues,
  defaultAllowableFilterSizes,
  isMaxField,
  isMinField,
  pairSibling,
  readBound,
  resolveMaxFhValue,
  setBoundAttr,
  setCachedBound,
  type SizerBoundsState,
  type SizerCollections,
  type SizerFieldName,
  type SizerFieldValues,
  type SizerOption,
  type SizerOptionSets,
} from './sizerSettings';

/**
 * Phase 4b — the `/sizer/` settings-panel state machine: a 1:1 port of the
 * LIVE half of `django/sizer/static/sizer/mvc/view/main.js`.
 *
 * Ported handlers (discovery §10 "LIVE — must port"):
 *   initialize collection graph  :63-149   · updateMaxFH        :176-211
 *   render (template + restore)  :213-336  · changeInput        :376-401
 *   getDataDependsOnConstructionClass :403-424
 *   changeCached                 :426-465  · updateFieldsCache  :467-476
 *   updateMatrixData             :478-570  · applySizes         :791-804
 *   changeAllowableFilterSizes   :805-822  · hideAllowableFilterSizes :775-790
 *
 * WHY THIS IS REF-HEAVY: the Backbone original reads and writes the DOM
 * synchronously inside one event turn (two delegated handlers fire per change —
 * `changeInput` then `changeCached`, in `events`-hash binding order), and the
 * ORDER of those reads is observable (see `commitControl`). Refs are the honest
 * model of that mutable DOM; React state mirrors them for rendering.
 */

/** The 4c seam: one matrix POST body plus the tick that produced it. */
export interface SizerMatrixRequestState {
  payload: ISizerMatrixRequest;
  /** increments on EVERY `updateMatrixData()` moment, even when the body is
   * unchanged — legacy wipes `MatrixView.pages` wholesale each time
   * (matrix.js:92-100), so the tick, not the body, is the trigger. */
  revision: number;
}

export interface UseSizerSettings {
  /** false until every load-time collection has resolved. Legacy renders NO
   * `#ksdm-content` markup at all before that (`render()` is inside the
   * `Promise.all().then`, main.js:134-141) — the page really is blank. */
  ready: boolean;
  values: SizerFieldValues;
  /** live `data-min`/`data-max` ATTRIBUTES (rendered on the inputs). */
  bounds: SizerBoundsState['attrs'];
  options: SizerOptionSets;
  ccMaxFhOptions: SizerOption[];
  hcMaxFhOptions: SizerOption[];
  /** the NATIVE `max` attribute of `max_different_sizes` (applySizes :797). */
  maxDifferentSizesNativeMax: number;
  /** `.non-clickable` on the Casing Insulation select while the Product Class
   * re-fetch is in flight (main.js:407). */
  casingInsulationDisabled: boolean;
  /** the COMMITTED multiselect. */
  allowableFilterSizes: ISizerFilterSize[];
  /** the STAGING array (`changed_allowable_filter_sizes`). */
  stagedFilterSizes: ISizerFilterSize[];
  filterSizes: ISizerFilterSize[];
  /** `#allowable_filter_sizes_list.active`. */
  filterListOpen: boolean;
  /**
   * The "All" checkbox (`value=""`). ⚠ It is NOT derived from the staged count:
   * `render()` only ticks the individual committed boxes (main.js:282-284) and
   * `hideAllowableFilterSizes` unticks EVERY `[value]` box — the All box
   * included, since `value=""` still matches `[value]` (:779). Only
   * `changeAllowableFilterSizes` ever ticks it (:818). So a committed set that
   * happens to be all 10 sizes still shows All UNCHECKED after a render.
   */
  allFilterSizesChecked: boolean;
  /** `#apply-sizes.non-clickable` — its own state, not a derivation: the
   * template ships it disabled (settings.tmpl:255) and `hideAllowableFilterSizes`
   * re-disables it unconditionally (main.js:789). */
  applyDisabled: boolean;
  /** bumped when `isEllipsisActive()` should re-measure (main.js:755, :803). */
  ellipsisNonce: number;
  matrixRequest: SizerMatrixRequestState | null;
  /** 4c/4d payloads the matrix needs but the settings panel does not render. */
  cabinetDimensions: ReturnType<typeof useGetSizerCabinetDimensionsQuery>['data'];
  pagesCount: [number, number] | undefined;
  pageSize: [number, number] | undefined;
  deviation: ReturnType<typeof useGetSizerDeviationQuery>['data'];
  optimalSizes: ReturnType<typeof useGetSizerOptimalSizesQuery>['data'];

  // ---- handlers -----------------------------------------------------------
  /** `change` on any `.cached` control of the settings panel. */
  commitControl: (name: SizerFieldName, rawValue: string, kind: 'input' | 'select') => void;
  /** `change` on the airflow field's `changeInput` leg (main.js:376-401). */
  requestMatrixUpdate: () => void;
  toggleFilterList: () => void;
  hideFilterList: () => void;
  /** body-handler dismissal (main.js:170-171) — closes the list, no revert. */
  closeFilterList: () => void;
  changeAllowableFilterSizes: (value: string, checked: boolean) => void;
  applySizes: () => void;
  bumpEllipsis: () => void;
}

export interface UseSizerSettingsArgs {
  /** live read of `input[name=airflow]` — `updateMatrixData` reads the DOM, so
   * the payload must always see the CURRENT value, not a render-old one. */
  getAirflow: () => string;
  /**
   * `render(true)` finished after a Product Class change: legacy re-opens the
   * settings popup (main.js:417) and, 300 ms later, auto-clicks `.click_to_size`
   * (:276-281). The page owns both, so it is told here.
   */
  onProductClassRerendered?: () => void;
}

const EMPTY_OPTIONS: SizerOption[] = [];

export function useSizerSettings({
  getAirflow,
  onProductClassRerendered,
}: UseSizerSettingsArgs): UseSizerSettings {
  // ---- the localStorage bag (`self.sizerSettings`) -------------------------
  const cacheRef = useRef<SizerSettingsCache>(readSizerSettings());

  /** `updateFieldsCache` (main.js:467-476). */
  const updateFieldsCache = useCallback((overrides: Record<string, unknown>) => {
    for (const key in overrides) cacheRef.current[key] = overrides[key];
    writeSizerSettings(cacheRef.current);
  }, []);

  // ---- collections --------------------------------------------------------
  const { data: constructionClass } = useGetSizerConstructionClassesQuery();

  /**
   * `initialize` fetches ConstructionClass FIRST and only then the classed
   * collections, because a stale `construction_class_id` has to be reset to 1
   * before those URLs are built (main.js:101-108). `null` = not resolved yet.
   */
  const [ccId, setCcId] = useState<string | null>(null);
  useEffect(() => {
    if (!constructionClass || ccId !== null) return;
    const cached = cacheRef.current.construction_class_id;
    if (cached && cached != 1) {
      const exists = constructionClass.some((e) => e.id == cached);
      if (!exists) {
        cacheRef.current.construction_class_id = 1;
        writeSizerSettings(cacheRef.current);
        setCcId('1');
        return;
      }
    }
    // DEVIATION (documented, unreachable in practice): legacy would interpolate
    // a missing key straight into the URL (`?construction_class_id=undefined`).
    // Every writer preserves the key, so only hand-edited storage gets there.
    setCcId(cached == null ? '1' : String(cached));
  }, [constructionClass, ccId]);

  const ccArg = ccId ?? skipToken;
  /**
   * ★ `currentData`, NOT `data` — for every ARG-DEPENDENT query (4c bugfix).
   *
   * RTK Query's `data` is "the latest successful result for ANY arg this hook
   * has had", so while the new construction class is in flight these three keep
   * returning the OLD class's payload. Legacy is the opposite: it AWAITS all
   * three fetches and only then calls `render(true)` (main.js:408-416), which
   * throws away `#ksdm-content` and builds a brand-new MatrixView.
   *
   * The stale window is directly observable. MEASURED before the fix: a Product
   * Class change issued **8** matrix POSTs where legacy issues **4** — the
   * revision produced while `cabinetDimensions` still held the class-1 object
   * was applied once by the old `SizerMatrix` (still keyed 1), then AGAIN by the
   * remounted one when the class-5 dimensions finally landed and flipped its
   * key. `currentData` makes the transition look like legacy's: the matrix
   * unmounts, the settings panel keeps its old options exactly as legacy's
   * un-re-rendered DOM does, and ONE `update_data` runs against the new class.
   * VERIFIED after the fix: 4 POSTs, one engine, matching legacy on dev3.
   *
   * The same applies to the Fin-Height query below, where legacy's
   * `updateMaxFH(...).then(...)` sequencing means the select must NOT be
   * repopulated from the previous tube diameter's rows.
   */
  const { currentData: cabinetDimensions } = useGetSizerCabinetDimensionsQuery(ccArg);
  const { currentData: pagesCount } = useGetSizerPagesCountQuery(ccArg);
  const { currentData: casingInsulations } = useGetSizerCasingInsulationsQuery(ccArg);
  const { data: pageSize } = useGetSizerPageSizeQuery();
  const { data: filterSizes } = useGetSizerFilterSizesQuery();
  const { data: casingFlangeWidths } = useGetSizerCasingFlangeWidthsQuery();
  const { data: coolingCoilSettings } = useGetSizerCoolingCoilSettingsQuery();
  const { data: heatingCoilSettings } = useGetSizerHeatingCoilSettingsQuery();
  const { data: optimalSizes } = useGetSizerOptimalSizesQuery();
  const { data: deviation } = useGetSizerDeviationQuery();
  const { data: tubeDias } = useGetSizerTubeDiasQuery();

  const collectionsReady =
    !!constructionClass &&
    !!casingInsulations &&
    !!filterSizes &&
    !!casingFlangeWidths &&
    !!tubeDias &&
    coolingCoilSettings !== undefined &&
    heatingCoilSettings !== undefined &&
    cabinetDimensions !== undefined &&
    pagesCount !== undefined &&
    deviation !== undefined &&
    optimalSizes !== undefined &&
    pageSize !== undefined;

  const collections: SizerCollections | null = useMemo(() => {
    if (!collectionsReady) return null;
    return {
      constructionClass: constructionClass ?? [],
      casingInsulations: casingInsulations ?? [],
      tubeDias: tubeDias ?? [],
      casingFlangeWidths: casingFlangeWidths ?? [],
      coolingCoilSettings: coolingCoilSettings ?? null,
      heatingCoilSettings: heatingCoilSettings ?? null,
    };
  }, [
    collectionsReady,
    constructionClass,
    casingInsulations,
    tubeDias,
    casingFlangeWidths,
    coolingCoilSettings,
    heatingCoilSettings,
  ]);

  const options = useMemo<SizerOptionSets>(
    () =>
      collections
        ? buildOptionSets(collections)
        : {
            constructionClass: [],
            casingInsulation: [],
            ccTube: [],
            hcTube: [],
            ccFlange: [],
            hcFlange: [],
          },
    [collections]
  );
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // ---- the allowable-filter-sizes multiselect ------------------------------
  // main.js:96-99 — a cached commit is adopted in `initialize` (i.e. BEFORE any
  // fetch) and immediately re-persisted (an identical write, but a write).
  const [allowableFilterSizes, setAllowableFilterSizes] = useState<ISizerFilterSize[]>(() => {
    const cached = cacheRef.current.allowable_filter_sizes;
    return Array.isArray(cached) ? cached : [];
  });
  const allowableRef = useRef(allowableFilterSizes);
  allowableRef.current = allowableFilterSizes;
  const seededAllowableRef = useRef(false);
  useEffect(() => {
    if (seededAllowableRef.current) return;
    const cached = cacheRef.current.allowable_filter_sizes;
    if (Array.isArray(cached)) {
      seededAllowableRef.current = true;
      updateFieldsCache({ allowable_filter_sizes: cached });
    }
  }, [updateFieldsCache]);

  const [stagedFilterSizes, setStagedFilterSizes] = useState<ISizerFilterSize[]>([]);
  const stagedRef = useRef(stagedFilterSizes);
  stagedRef.current = stagedFilterSizes;
  /** `self.applied` (main.js:778, :784, :794). */
  const appliedRef = useRef(false);
  const [filterListOpen, setFilterListOpen] = useState(false);
  const [allFilterSizesChecked, setAllFilterSizesChecked] = useState(false);
  const [applyDisabled, setApplyDisabled] = useState(true);
  const [ellipsisNonce, setEllipsisNonce] = useState(0);
  const bumpEllipsis = useCallback(() => setEllipsisNonce((n) => n + 1), []);

  // ---- values / bounds ----------------------------------------------------
  const [values, setValuesState] = useState<SizerFieldValues | null>(null);
  const valuesRef = useRef<SizerFieldValues | null>(null);
  const setValues = useCallback((next: SizerFieldValues) => {
    valuesRef.current = next;
    setValuesState(next);
  }, []);

  /** jQuery's mutable `data`/attribute pair — see `SizerBoundsState`. */
  const boundsRef = useRef<SizerBoundsState | null>(null);
  const [boundsAttrs, setBoundsAttrs] = useState<SizerBoundsState['attrs'] | null>(null);
  const syncBounds = useCallback(() => {
    if (boundsRef.current) setBoundsAttrs({ ...boundsRef.current.attrs });
  }, []);

  const [maxDifferentSizesNativeMax, setMaxDifferentSizesNativeMax] = useState(6);
  const [casingInsulationDisabled, setCasingInsulationDisabled] = useState(false);

  // ---- matrix tick --------------------------------------------------------
  const [matrixRequest, setMatrixRequest] = useState<SizerMatrixRequestState | null>(null);
  const revisionRef = useRef(0);
  const getAirflowRef = useRef(getAirflow);
  getAirflowRef.current = getAirflow;

  /**
   * `updateMatrixData()` (main.js:478-570). `valuesOverride` exists to
   * reproduce a REAL ordering quirk: `changeInput` (which fires this) is bound
   * BEFORE `changeCached` (which clamps), so the body carries the value AS
   * TYPED — an out-of-range entry reaches the server once, and only the next
   * change sees the clamped value.
   */
  const pushMatrixUpdate = useCallback(
    (valuesOverride?: SizerFieldValues, allowableOverride?: ISizerFilterSize[]) => {
      const v = valuesOverride ?? valuesRef.current;
      if (!v) return;
      const payload = buildMatrixPayload({
        airflow: getAirflowRef.current(),
        values: v,
        options: optionsRef.current,
        casingInsulations: casingInsulations ?? [],
        allowableFilterSizes: allowableOverride ?? allowableRef.current,
      });
      revisionRef.current += 1;
      setMatrixRequest({ payload, revision: revisionRef.current });
    },
    [casingInsulations]
  );

  // ---- render() — template values, then the localStorage restore -----------
  /**
   * One "render generation" per legacy `render()` call: the initial load
   * (main.js:141) and every Product Class change (`render(true)`, :416). Both
   * rebuild the whole settings DOM from the template and then re-apply the
   * cache, which is why a fresh generation also resets jQuery's `data` store
   * (brand-new elements ⇒ empty store — see `SizerBoundsState`).
   */
  const [generation, setGeneration] = useState(0);
  const renderedGenRef = useRef<string | null>(null);
  const pendingProductClassRef = useRef(false);

  useEffect(() => {
    if (!collections || !ccId) return;
    const key = `${generation}:${ccId}`;
    if (renderedGenRef.current === key) return;
    renderedGenRef.current = key;

    // main.js:249 — the multiselect default is computed at RENDER time, only
    // when nothing has been committed yet.
    if (!allowableRef.current.length) {
      const next = defaultAllowableFilterSizes(filterSizes ?? []);
      allowableRef.current = next;
      setAllowableFilterSizes(next);
    }
    // hideAllowableFilterSizes' staging reset is implied by a fresh render.
    stagedRef.current = [...allowableRef.current];
    setStagedFilterSizes(stagedRef.current);
    appliedRef.current = false;
    setAllFilterSizesChecked(false); // :282-284 tick individuals only
    setApplyDisabled(true); // settings.tmpl:255 ships `non-clickable`
    setFilterListOpen(false);

    const templateValues = buildTemplateValues(collections, options);
    const templateBounds = buildTemplateBounds(collections);
    const restored = applyCachedValues(
      templateValues,
      templateBounds,
      options,
      cacheRef.current as Record<string, unknown>
    );
    boundsRef.current = { attrs: restored.bounds, cache: {} };
    setBoundsAttrs({ ...restored.bounds });
    setMaxDifferentSizesNativeMax(6);
    setValues(restored.values);
    setCasingInsulationDisabled(false);
    // Both Max-FH selects must be (re)filled for this generation before the
    // single trailing `updateMatrixData()` fires (main.js:142-147 / :418-422).
    fhAppliedRef.current = { cc: null, hc: null };
    pendingRenderMatrixRef.current = true;
    if (pendingProductClassRef.current) {
      pendingProductClassRef.current = false;
      onProductClassRerendered?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collections, ccId, generation, options, filterSizes]);

  // ---- updateMaxFH (main.js:176-211) --------------------------------------
  const ccTubeValue = values?.cc_tube ?? '';
  const hcTubeValue = values?.hc_tube ?? '';
  // `currentData` — see the note above: with `data`, a tube change fires the
  // Max-FH effect against the PREVIOUS tube's rows and latches
  // `fhAppliedRef` to the new tube id, so the real rows are then skipped and
  // the select keeps the wrong options.
  const { currentData: ccFhRows } = useGetSizerCoilFhsQuery(ccTubeValue || skipToken);
  const { currentData: hcFhRows } = useGetSizerCoilFhsQuery(hcTubeValue || skipToken);

  const ccMaxFhOptions = useMemo(
    () => (ccFhRows ? buildMaxFhOptions(ccFhRows, 'cc') : EMPTY_OPTIONS),
    [ccFhRows]
  );
  const hcMaxFhOptions = useMemo(
    () => (hcFhRows ? buildMaxFhOptions(hcFhRows, 'hc') : EMPTY_OPTIONS),
    [hcFhRows]
  );

  /** which tube id each side's Max-FH select was last rebuilt for. */
  const fhAppliedRef = useRef<{ cc: string | null; hc: string | null }>({ cc: null, hc: null });
  /** set by a tube change so the trailing `updateMatrixData()` waits for the
   * refreshed Max-FH (main.js:394-397 — `.then(updateMatrixData)`). */
  const pendingTubeMatrixRef = useRef(false);
  /** set by every render generation — its trailing `updateMatrixData()` also
   * waits for BOTH Max-FH selects (main.js:142-147 load, :418-422 cc change). */
  const pendingRenderMatrixRef = useRef(false);

  const applyMaxFh = useCallback(
    (side: 'cc' | 'hc', rows: ISizerMasterRow[], opts: SizerOption[]) => {
      const current = valuesRef.current;
      if (!current) return;
      const field: SizerFieldName = side === 'cc' ? 'cc_max_fh' : 'hc_max_fh';
      const next = {
        ...current,
        [field]: resolveMaxFhValue(opts, rows, side === 'cc', cacheRef.current[field]),
      };
      setValues(next);
    },
    [setValues]
  );

  useEffect(() => {
    if (!values || !ccTubeValue || !ccFhRows) return;
    if (fhAppliedRef.current.cc === ccTubeValue) return;
    fhAppliedRef.current.cc = ccTubeValue;
    applyMaxFh('cc', ccFhRows, ccMaxFhOptions);
  }, [values, ccTubeValue, ccFhRows, ccMaxFhOptions, applyMaxFh]);

  useEffect(() => {
    if (!values || !hcTubeValue || !hcFhRows) return;
    if (fhAppliedRef.current.hc === hcTubeValue) return;
    fhAppliedRef.current.hc = hcTubeValue;
    applyMaxFh('hc', hcFhRows, hcMaxFhOptions);
  }, [values, hcTubeValue, hcFhRows, hcMaxFhOptions, applyMaxFh]);

  /**
   * The trailing `updateMatrixData()` of both legacy sequences:
   *   load        `updateMaxFH(true).then(updateMaxFH(false)).then(updateMatrixData)` (:142-147)
   *   tube change `updateMaxFH(side).then(updateMatrixData)` (:394-397)
   * Fires once both sides have been filled for the current generation.
   */
  useEffect(() => {
    if (!valuesRef.current) return;
    if (!pendingRenderMatrixRef.current && !pendingTubeMatrixRef.current) return;
    const applied = fhAppliedRef.current;
    if (applied.cc !== ccTubeValue || applied.hc !== hcTubeValue) return;
    pendingRenderMatrixRef.current = false;
    pendingTubeMatrixRef.current = false;
    pushMatrixUpdate();
  }, [values, ccTubeValue, hcTubeValue, pushMatrixUpdate]);

  // ---- getDataDependsOnConstructionClass (main.js:403-424) -----------------
  const changeProductClass = useCallback(
    (rawValue: string) => {
      cacheRef.current.construction_class_id = rawValue;
      writeSizerSettings(cacheRef.current);
      setCasingInsulationDisabled(true); // :407
      pendingProductClassRef.current = true;
      setCcId(rawValue);
      setGeneration((g) => g + 1);
    },
    []
  );

  // ---- changeCached (main.js:426-465) + changeInput (main.js:376-401) ------
  const commitControl = useCallback(
    (name: SizerFieldName, rawValue: string, kind: 'input' | 'select') => {
      const current = valuesRef.current;
      const bounds = boundsRef.current;
      if (!current || !bounds) return;

      // --- 1. `change .sizer-container__items input|select` → changeInput ---
      // Bound BEFORE `change .cached`, so it sees the value AS TYPED.
      if (name === 'construction_class_id') {
        // changeInput early-returns (:379); changeCached short-circuits into the
        // Product Class cascade (:433-436) and caches nothing else.
        changeProductClass(rawValue);
        return;
      }
      const typedValues: SizerFieldValues = { ...current, [name]: rawValue };
      if (name === 'cc_tube' || name === 'hc_tube') {
        // :394-397 — `~name.lastIndexOf('c_tube')` matches BOTH tube selects;
        // the side comes from `name[0] == 'c'`. Refresh Max-FH, THEN re-POST.
        pendingTubeMatrixRef.current = true;
      } else {
        pushMatrixUpdate(typedValues);
      }

      // --- 2. `change .cached` → changeCached -------------------------------
      const needCache: Record<string, unknown> = {};
      let committed: string | number = rawValue;
      let nextValues: SizerFieldValues = typedValues;

      if (kind === 'input') {
        const min = readBound(bounds, name, 'min');
        const max = readBound(bounds, name, 'max');
        if (Number(committed) > max) committed = max;
        if (Number(committed) < min) committed = min;
        nextValues = { ...current, [name]: String(committed) };

        const sibling = pairSibling(name);
        if (isMinField(name) && sibling) {
          setBoundAttr(bounds, sibling, 'min', String(committed));
          if (Number(nextValues[sibling]) < Number(committed)) {
            nextValues = { ...nextValues, [sibling]: String(committed) };
            needCache[name.replace('_min', '_max')] = committed;
          }
        } else if (isMaxField(name) && sibling) {
          setBoundAttr(bounds, sibling, 'max', String(committed));
          if (Number(nextValues[sibling]) > Number(committed)) {
            nextValues = { ...nextValues, [sibling]: String(committed) };
            needCache[name.replace('_max', '_min')] = committed;
          }
        }
        syncBounds();
      }

      // :461-463 — the control's own value, plus `_id`/`_value` from the
      // selected option's data attributes (selects only).
      needCache[name] = String(committed);
      if (kind === 'select') {
        const optionList = selectOptionsFor(name, optionsRef.current, ccMaxFhOptions, hcMaxFhOptions);
        const opt = optionList.find((o) => o.value == rawValue);
        if (opt && opt.dataId) needCache[`${name}_id`] = Number(opt.dataId);
        if (opt && opt.dataValue) needCache[`${name}_value`] = Number(opt.dataValue);
      }

      setValues(nextValues);
      updateFieldsCache(needCache);
    },
    [
      changeProductClass,
      pushMatrixUpdate,
      setValues,
      syncBounds,
      updateFieldsCache,
      ccMaxFhOptions,
      hcMaxFhOptions,
    ]
  );

  const requestMatrixUpdate = useCallback(() => pushMatrixUpdate(), [pushMatrixUpdate]);

  // ---- multiselect handlers -----------------------------------------------
  /** `hideAllowableFilterSizes` (main.js:775-790). */
  const hideFilterList = useCallback(() => {
    setFilterListOpen(false);
    // Without an Apply, EVERY `[value]` box is unticked (the All box included,
    // :779) and the committed ones re-ticked (:780-782). After an Apply the
    // revert is skipped, so the user's boxes — All included — survive as-is.
    if (!appliedRef.current) setAllFilterSizesChecked(false);
    appliedRef.current = false; // :784
    // The staging array is reset either way (:786).
    stagedRef.current = [...allowableRef.current];
    setStagedFilterSizes(stagedRef.current);
    setApplyDisabled(true); // :789
  }, []);

  /**
   * The blanket BODY click handler's multiselect branch (main.js:168-171).
   *
   * ⚠ It is NOT `hideAllowableFilterSizes`. Despite the local variable there
   * being named `hideAllowableFilterSizes`, the body handler's whole body is
   *   `$('.allowable_filter_sizes_list.active').removeClass('active')`
   * — it closes the list and nothing else. The revert-to-committed method
   * (main.js:775-790) is reachable only from `toggleMultiSelect` (:766-774)
   * and `applySizes` (:802).
   *
   * So dismissing the list by clicking outside it PRESERVES the staged (not
   * yet applied) checkboxes and leaves Apply enabled — verified on dev3: after
   * staging a 3rd size and clicking the popup heading, legacy still shows
   * boxes `1,2,3` ticked with `#apply-sizes` class `btn`. Reverting here made
   * the user's unapplied staging silently disappear on reopen.
   */
  const closeFilterList = useCallback(() => {
    setFilterListOpen(false);
  }, []);

  /** `toggleMultiSelect` (main.js:766-774). */
  const toggleFilterList = useCallback(() => {
    if (filterListOpen) hideFilterList();
    else setFilterListOpen(true);
  }, [filterListOpen, hideFilterList]);

  /** `changeAllowableFilterSizes` (main.js:805-822). The "All" box has
   * `value=""`; it sets/clears everything and is itself auto-ticked by the
   * component when the staged count equals the total. */
  const changeAllowableFilterSizes = useCallback(
    (value: string, checked: boolean) => {
      const all = filterSizes ?? [];
      let next: ISizerFilterSize[];
      if (!value) {
        // :809-811 — the All box replaces the staging array wholesale. Note it
        // assigns `self.filterAvaiableSizes` BY REFERENCE, not a copy.
        next = checked ? all : [];
        setAllFilterSizesChecked(checked);
      } else if (checked) {
        // :814 — a plain push; legacy never de-duplicates (unreachable, the
        // box cannot be checked twice).
        const row = all.find((el) => el.id == Number(value));
        next = row ? [...stagedRef.current, row] : [...stagedRef.current];
        setAllFilterSizesChecked(next.length === all.length); // :818
      } else {
        next = stagedRef.current.filter((el) => el.id != Number(value));
        setAllFilterSizesChecked(next.length === all.length); // :818
      }
      stagedRef.current = next;
      setStagedFilterSizes(next);
      setApplyDisabled(next.length < 1); // :820-821
    },
    [filterSizes]
  );

  /** `applySizes` (main.js:791-804). */
  const applySizes = useCallback(() => {
    const bounds = boundsRef.current;
    const current = valuesRef.current;
    if (!bounds || !current) return;
    const committedSizes = [...stagedRef.current];
    allowableRef.current = committedSizes;
    setAllowableFilterSizes(committedSizes);
    appliedRef.current = true;

    let working = current;
    if (committedSizes.length < Number(current.max_different_sizes)) {
      // :796 — `.val(n).change()`: a REAL change event, so `changeInput` fires
      // an extra `updateMatrixData()` (with the already-committed sizes) and
      // `changeCached` caches the clamped value. Reproduced as its own tick.
      const n = String(committedSizes.length);
      pushMatrixUpdate({ ...current, max_different_sizes: n }, committedSizes);
      const min = readBound(bounds, 'max_different_sizes', 'min');
      const max = readBound(bounds, 'max_different_sizes', 'max');
      let clamped: string | number = n;
      if (Number(clamped) > max) clamped = max;
      if (Number(clamped) < min) clamped = min;
      working = { ...current, max_different_sizes: String(clamped) };
      setValues(working);
      updateFieldsCache({ max_different_sizes: String(clamped) });
      // :797-798 — the NATIVE `max` attribute AND jQuery's `data` store; the
      // `data-max` ATTRIBUTE is deliberately left at 6.
      setMaxDifferentSizesNativeMax(committedSizes.length);
      setCachedBound(bounds, 'max_different_sizes', 'max', committedSizes.length);
    }

    hideFilterList();
    updateFieldsCache({ allowable_filter_sizes: committedSizes });
    pushMatrixUpdate(working, committedSizes);
    bumpEllipsis();
  }, [bumpEllipsis, hideFilterList, pushMatrixUpdate, setValues, updateFieldsCache]);

  return {
    ready: !!values && !!boundsAttrs,
    values: values ?? EMPTY_VALUES,
    bounds: boundsAttrs ?? EMPTY_BOUNDS,
    options,
    ccMaxFhOptions,
    hcMaxFhOptions,
    maxDifferentSizesNativeMax,
    casingInsulationDisabled,
    allowableFilterSizes,
    stagedFilterSizes,
    filterSizes: filterSizes ?? [],
    filterListOpen,
    allFilterSizesChecked,
    applyDisabled,
    ellipsisNonce,
    matrixRequest,
    cabinetDimensions,
    pagesCount,
    pageSize,
    deviation,
    optimalSizes,
    commitControl,
    requestMatrixUpdate,
    toggleFilterList,
    hideFilterList,
    closeFilterList,
    changeAllowableFilterSizes,
    applySizes,
    bumpEllipsis,
  };
}

/** The option list backing a given select (needed for the `_id`/`_value` cache). */
function selectOptionsFor(
  name: SizerFieldName,
  options: SizerOptionSets,
  ccMaxFh: SizerOption[],
  hcMaxFh: SizerOption[]
): SizerOption[] {
  switch (name) {
    case 'construction_class_id':
      return options.constructionClass;
    case 'casing_insulation':
      return options.casingInsulation;
    case 'cc_tube':
      return options.ccTube;
    case 'hc_tube':
      return options.hcTube;
    case 'cc_top_and_btm_casing_flange':
      return options.ccFlange;
    case 'hc_top_and_btm_casing_flange':
      return options.hcFlange;
    case 'cc_max_fh':
      return ccMaxFh;
    case 'hc_max_fh':
      return hcMaxFh;
    default:
      return EMPTY_OPTIONS;
  }
}

const EMPTY_VALUES: SizerFieldValues = {
  construction_class_id: '',
  casing_insulation: '',
  cc_face_vel_min: '',
  cc_face_vel_max: '',
  cc_tube: '',
  cc_max_fh: '',
  cc_top_and_btm_casing_flange: '',
  cc_qty_stacked: '',
  hc_face_vel_min: '',
  hc_face_vel_max: '',
  hc_tube: '',
  hc_max_fh: '',
  hc_top_and_btm_casing_flange: '',
  hc_qty_stacked: '',
  max_different_sizes: '',
  filter_face_vel_min: '',
  filter_face_vel_max: '',
};

const EMPTY_BOUNDS: SizerBoundsState['attrs'] = Object.fromEntries(
  Object.keys(EMPTY_VALUES).map((k) => [k, { min: null, max: null }])
) as SizerBoundsState['attrs'];
